import { useState } from 'react';

import { HttpRequest, IHttpClient } from 'app/libs/http-client';
import { Fail } from 'app/libs/http-client/fails/fail';
import { useAuthStatusWriter } from 'app/railways/domains/auth/features/auth-status';

import { HttpResource } from '../http-resource';
import { INotifications } from '../notifications/core/notifications';
import { useApiClient } from './api-client.provider';

type OptFnArg<Param, Result> = [Param] extends [void]
  ? () => Result
  : (arg: Param) => Result;

type UseResourceParams<Params, Done, Fail> = {
  request: OptFnArg<Params, HttpRequest>;
  client?: IHttpClient;
  errorNotify?: INotifications;
  onDone?: (result: Done) => void;
  onAuthRequired?: (error: Fail) => void;
  onFail?: (error: Fail) => void;
};

export const useResource = <Params, Done>({
  request,
  client,
  errorNotify,
  onDone,
  onAuthRequired,
  onFail,
}: UseResourceParams<Params, Done, Fail>) => {
  const apiClient = useApiClient();
  const authStatus = useAuthStatusWriter();

  const [resource] = useState(
    () =>
      new HttpResource<Params, Done>({
        httpRequest: request,
        apiClient: client ? client : apiClient,
        errorNotify: errorNotify,
        onAuthRequired: (error) =>
          onAuthRequired ? onAuthRequired(error) : authStatus.denyAccess(),
        onFinish: onDone,
        onError: onFail,
      })
  );

  return resource;
};
